"use client";

import * as React from "react";
import ShellFrame from "./ShellFrame";
import IndexShellContext from "./IndexShellContext";
import { usePersistedWidth } from "./shell-primitives";
import { useIsCompactShell } from "@/hooks/useViewport";

/**
 * 인덱스 페이지(`/theory`)용 셸 동등 3컬럼 레이아웃 (JCG-2097 / JCG-2142 ShellFrame 이주).
 *
 * 글 페이지의 ArticleShell 은 지도·목차·필기·점수·풀이 머신이 결합돼 인덱스 페이지에 부적합하므로,
 * 카드형 랜딩 전용으로 가벼운 3컬럼 셸을 둔다. 좌: IconRail(전역 네비) + 카드 슬롯 / 가운데: 본문
 * (타이틀·인트로·대표 배너) / 우: 카드 슬롯. 좌/우 사이드바 카드는 페이지가 슬롯에 createPortal.
 *
 * 셸 chrome(IconRail·sticky 좌/우 aside·그리드·드래그 리사이즈)은 ShellFrame 이 단일 소유한다.
 * 이 wrapper 는 ShellFrame thin wrapper 로서 ① 폭 영속(ui2-index-*) ② 슬롯 div(portal 타깃)
 * ③ 가운데 상단 고정 헤더 슬롯(staticTitle 핀이 없는 인덱스 페이지 전용) 만 소유한다.
 *
 * - 데스크탑(≥1024px)에서만 LayoutSwitch 가 렌더 — 모바일/태블릿은 클래식(세로 스택) 폴백.
 *   폴백은 Provider 부재(inShell:false) → 페이지가 카드를 본문 흐름에 인라인 렌더(SSR-safe).
 * - 인덱스 페이지(`/theory`)는 `isSidebarAdHidden`=true 라 광고를 두지 않는다(슬롯=카드 전용).
 * - 폭은 state + localStorage 영속(`ui2-index-left-w`/`ui2-index-right-w`) + 드래그 리사이즈.
 *   ArticleShell 의 article 폭 키(ui2-left-w 등)와 병합 금지 — 별도 네임스페이스.
 * - 스크롤 모델은 ArticleShell 과 동일: 사이드바 `sticky top-0 h-dvh self-start`(슬롯 내부 스크롤),
 *   가운데는 일반 문서(window) 스크롤.
 */
const LEFT_MIN = 320;
const LEFT_MAX = 520;
const LEFT_DEFAULT = 360;
const RIGHT_MIN = 280;
const RIGHT_MAX = 480;
const RIGHT_DEFAULT = 340;
const LS_LEFT = "ui2-index-left-w";
const LS_RIGHT = "ui2-index-right-w";

interface IndexShellProps {
  children: React.ReactNode;
  /** 서버 컴포넌트 (Footer) */
  footer: React.ReactNode;
  /** 서버 컴포넌트 (전역 핸들러) */
  globals: React.ReactNode;
}

export default function IndexShell({ children, footer, globals }: IndexShellProps) {
  // 서버 기본폭 렌더 → 마운트 후 LS 복원(lazy init 금지). setter 는 clamp+영속 일체(JCG-2138).
  const [leftW, onResizeLeft] = usePersistedWidth(LS_LEFT, LEFT_DEFAULT, LEFT_MIN, LEFT_MAX);
  const [rightW, onResizeRight] = usePersistedWidth(
    LS_RIGHT,
    RIGHT_DEFAULT,
    RIGHT_MIN,
    RIGHT_MAX,
  );

  // 사이드바/헤더 카드 portal 타깃 — 페이지가 createPortal 로 자기 카드를 그린다.
  const [headerSlot, setHeaderSlot] = React.useState<HTMLElement | null>(null);
  const [leftSlot, setLeftSlot] = React.useState<HTMLElement | null>(null);
  const [rightSlot, setRightSlot] = React.useState<HTMLElement | null>(null);

  const slots = React.useMemo(
    () => ({ inShell: true, headerSlot, leftSlot, rightSlot }),
    [headerSlot, leftSlot, rightSlot],
  );

  // 컴팩트 셸(1024~1279px, JCG-2224): 카드 사이드바를 최소폭(좌 320·우 280)으로 양보해
  // 1024px에서 본문 ~424px 확보. 영속값(localStorage)은 불변, 렌더 시 유효 폭만 클램프한다.
  const compact = useIsCompactShell();
  const effLeftW = compact ? Math.min(leftW, LEFT_MIN) : leftW;
  const effRightW = compact ? Math.min(rightW, RIGHT_MIN) : rightW;

  return (
    <IndexShellContext.Provider value={slots}>
      <ShellFrame
        columns="left+main+right"
        leftWidthCss={`${effLeftW}px`}
        onResizeLeft={onResizeLeft}
        rightWidthCss={`${effRightW}px`}
        onResizeRight={onResizeRight}
        // 좌측 카드 슬롯 (IconRail 옆)
        left={<div ref={setLeftSlot} className="min-w-0 flex-1 overflow-y-auto p-3" />}
        // 우측 카드 슬롯
        right={<div ref={setRightSlot} className="min-w-0 flex-1 overflow-y-auto p-3" />}
        // 가운데 상단 고정 바 — 페이지가 타이틀을 portal. 비었으면 empty:hidden 으로 사라짐(폴백 시).
        // ContentHeader 핀 scroll-spy 대상 h1 이 없는 인덱스 전용이라 renderHeader 로 직접 렌더.
        renderHeader={
          <div
            ref={setHeaderSlot}
            className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 empty:hidden"
          />
        }
      >
        <div className="mx-auto w-full max-w-[820px] px-4 pb-20 pt-4 sm:px-8">
          {children}
          {footer}
        </div>
      </ShellFrame>

      {globals}
    </IndexShellContext.Provider>
  );
}
